import { FC, useState } from 'react'

import styled from 'styled-components'
import { Divider } from 'antd'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'

import { CuratorInfoItem } from './CuratorInfoItem'
import { PlaylistInfoItem } from './PlaylistInfoItem'
import { ShowAllPlaylistsButton } from './ShowAllPlaylistsButton'

interface Playlist {
  id: number
  matchedGenres: string[]
  playlistCover: string
  playlistUrl: string
  playlistName?: string
  followers?: number
}

interface CuratorPlaylistsItemProps {
  curatorName: string
  playlistCount: number
  price: string
  selected: boolean
  avatarUrl?: string | null
  playlists: Playlist[]
  onSelect?: () => void
  matchingPercent?: number
  madePlacements?: boolean
  isSaved?: boolean
  lastActivity?: string | null
}

const Container = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  border: ${({ $selected, theme }) =>
    $selected
      ? `1px solid ${theme.color.brandGreen}`
      : `1px solid ${theme.color.lightGrey}`};
  border-radius: 16px;
  padding: 24px;
  width: 100%;
`

const PlaylistsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 24px;
  }
`

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const StyledDivider = styled(Divider)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const MAX_PLAYLISTS_DISPLAYED = 3
export const CuratorPlaylistsItem: FC<CuratorPlaylistsItemProps> = ({
  curatorName,
  playlistCount,
  price,
  selected,
  avatarUrl,
  playlists,
  onSelect,
  matchingPercent,
  madePlacements,
  isSaved,
  lastActivity,
}) => {
  const [showAll, setShowAll] = useState(false)

  const hasMoreThanThreePlaylists = playlists.length > MAX_PLAYLISTS_DISPLAYED
  const displayedPlaylists =
    !showAll && hasMoreThanThreePlaylists
      ? playlists.slice(0, MAX_PLAYLISTS_DISPLAYED)
      : playlists
  return (
    <Container $selected={selected}>
      <CuratorInfoItem
        curatorName={curatorName}
        playlistCount={playlistCount}
        price={price}
        selected={selected}
        avatarUrl={avatarUrl}
        onSelect={onSelect}
        matchingPercent={matchingPercent}
        madePlacements={madePlacements}
        isSaved={isSaved}
        lastActivity={lastActivity ?? void 0}
      />

      <StyledDivider dashed />

      <PlaylistsSection>
        {displayedPlaylists.map((playlist) => (
          <PlaylistInfoItem
            key={playlist.id}
            playlistName={playlist.playlistName || ''}
            followers={playlist.followers || 0}
            genres={playlist.matchedGenres}
            coverUrl={playlist.playlistCover}
            playlistUrl={playlist.playlistUrl}
          />
        ))}
      </PlaylistsSection>

      {hasMoreThanThreePlaylists && (
        <ButtonSection>
          <ShowAllPlaylistsButton
            showAll={showAll}
            onClick={() => setShowAll(!showAll)}
          />
        </ButtonSection>
      )}
    </Container>
  )
}
